/**
 * "Recovered" notice of the Library (W2-10 projects-data; DESIGN.md §4.1, §5.13): charts whose previous session did
 * not close cleanly (crash, killed tab) had their latest edits replayed from the journal. Nothing is lost; the
 * notice says so and offers to open them. It can be dismissed (until the page is reloaded).
 */

import { useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ProjectSummary } from '../../persist/types';
import { Button, Icon, IconButton } from '../../ui';
import type { LibraryText } from './hooks';
import { m } from './library.messages';
import { openProject } from './libraryActions';
import { levelOf, titleOf } from './ProjectCard';
import s from './Library.module.css';

/** Charts listed with an Open button (the rest are counted only). */
const SHOWN = 3;

export interface RecoveryNoticeProps {
  readonly services: AppServices;
  readonly text: LibraryText;
  readonly projects: readonly ProjectSummary[];
}

export function RecoveryNotice(props: RecoveryNoticeProps) {
  const { services, text } = props;
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  const recovered = props.projects.filter((p) => p.recoverable && p.trashedAt === null && !dismissed.has(p.id));
  if (recovered.length === 0) return null;
  return (
    <section className={s.notice} data-tone="info" role="status" aria-labelledby="library-recovery-title">
      <Icon name="History" size={20} />
      <div className={s.noticeText}>
        <h3 id="library-recovery-title" className={s.noticeTitle}>
          {text.t(m.recoveryTitle, { count: recovered.length })}
        </h3>
        <p>{text.t(m.recoveryBody)}</p>
        <div className={s.noticeActions}>
          {recovered.slice(0, SHOWN).map((p) => (
            <Button
              key={p.id}
              size="sm"
              icon="FolderOpen"
              onClick={() => {
                void openProject(services, p);
              }}
            >
              {text.t(m.recoveryOpen, { name: `${titleOf(text, p)} ${levelOf(text, p)}` })}
            </Button>
          ))}
        </div>
      </div>
      <IconButton
        icon="X"
        size="sm"
        label={{ key: m.dismiss }}
        onClick={() => {
          setDismissed(new Set([...dismissed, ...recovered.map((p) => p.id)]));
        }}
      />
    </section>
  );
}
